import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {withStyles} from "@mui/styles";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import moment from "moment-timezone";
import {withTranslation} from "react-i18next";
import ILocalAGGrid, {
  getDefaultColDef,
  createServerSideDatasource,
  withDateColumnFilter,
  attachReportServerSideDatasource,
  PHARMACY_REPORT_SORT_MAP,
} from "../../grid/iLocalAGGrid";
import getTypeByLang from "../../common/typeByLang";
import {generateCSV} from "../../../utils/GenerateCSV";
import {getDefaultDateRange, formatFromDateForAPI, formatToDateForAPI} from "../../../utils/datetime.utils";
import download from "../images/download-doc.svg";
import spinner from "../../assign-prescriptions/images/spinner.gif";
import { ReportToolbar } from "../ReportPageLayout";
import { applyControlledSubstanceReportFilterModel } from "./controlledSubstanceReportFilterModel";
import { resolveControlledSubstanceDispensingLocationOptions } from "./controlledSubstanceDispensingLocationOptions";


const styles = () => ({
  paper: {
    position: "relative",
  },
  table: {
    minWidth: 1020,
  },
  tableWrapper: {
    overflowX: "auto",
  },
  padding10px: {
    padding: "10px",
  },
  spinner: {
    position: "absolute",
    background: "rgba(255,255,255,.4)",
    width: "100%",
    height: "100%",
    zIndex: 9999,
    top: 0,
    left: 0,
    pointerEvents: "none",
  },
  spinnerImage: {
    top: "calc(50% - 32.5px)",
    left: "calc(50% - 65px)",
    position: "absolute",
    width: "130px",
    userSelect: "none",
  },
  primaryButton: {
    background: "#6cca98",
    color: "#fff",
    "&:hover": {
      background: "#6cca98",
      color: "#fff",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.38)",
      cursor: "not-allowed",
      background: "#E4E5E8",
    },
  },
});

class EnhancedTableHead extends React.Component {
  createSortHandler = (property) => (event) => {
    this.props.onRequestSort(event, property);
  };

  render() {
    const {order, orderBy, sortId, t} = this.props;
    const rows = [
      { id: "dispensing_location", label: t("Report.ControlledSubstance.DispensingLocation"), field: "store" },
      { id: "drugname", label: t("Report.ControlledSubstance.Item"), field: "drugname" },
      { id: "patient", label: t("Report.ControlledSubstance.Patient"), field: "firstname" },
      { id: "rxno", label: t("Report.ControlledSubstance.RxNumber"), field: "rxno" },
      { id: "dea_schedule", label: t("Report.ControlledSubstance.Schedule"), field: "dea_schedule" },
      { id: "pdrivers_license_no", label: t("Report.ControlledSubstance.IdNumber"), field: "pdrivers_license_no" },
      { id: "id_name", label: t("Report.ControlledSubstance.NameOnId"), field: "pfirst_name" },
      { id: "selectedstatecode", label: t("Report.ControlledSubstance.IssuingOrg"), field: "selectedstatecode" },
      { id: "expdate", label: t("Report.ControlledSubstance.IdExpiration"), field: "expdate" },
      { id: "card_type", label: t("Report.ControlledSubstance.IdType"), field: "card_type" },
      { id: "pickupdate", label: t("Report.ControlledSubstance.PickupDate"), field: "pickupdate" },
      { id: "care_team_member", label: t("Report.ControlledSubstance.CareTeam"), field: "username" },
      { id: "_id", label: t("Report.ControlledSubstance.IdImage"), sortable: false },
    ];

    return (
      <TableHead>
        <TableRow>
          {rows.map((row) => (
            <TableCell
              key={row.id}
              padding="default"
              sortDirection={orderBy === row.field ? order : false}
              style={{ padding: "10px", color: "rgba(0, 0, 0, 0.54)" }}>
              {row.sortable === false ? (
                <TableSortLabel style={{ cursor: "text" }}>{row.label}</TableSortLabel>
              ) : (
                <Tooltip
                  title={t("Global.Label.Filter.Sort")}
                  placement="bottom-start"
                  enterDelay={300}>
                  <TableSortLabel
                    active={orderBy === row.field && sortId === row.id}
                    direction={order}
                    onClick={this.createSortHandler(row.id)}>
                    {row.label}
                  </TableSortLabel>
                </Tooltip>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  sortId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

EnhancedTableHead = withTranslation()(EnhancedTableHead);

function getReportTimezone() {
  const stored = sessionStorage.getItem("timeZone");
  if (stored) return stored.substring(stored.lastIndexOf(" ") + 1);
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function buildFilterRef() {
  // Default to the last 7 days up front so the SSRM datasource can attach (and
  // seed the date filter UI) immediately on grid ready.
  const defaultDates = getDefaultDateRange();
  return {
    // Empty store_ids/box_ids: API scopes by account lineage (pickup-style).
    // Do not expand "All" into hundreds of box UUIDs — that 504s root users on Last 7 days.
    store_ids: [],
    box_ids: [],
    searchtext: "",
    fromdate: formatFromDateForAPI(defaultDates.fromDate),
    todate: formatToDateForAPI(defaultDates.toDate),
    pageNo: 0,
    pageSize: 10,
    sortOrder: "desc",
    sortColumn: "pickup_date",
  };
}


function authHeaders() {
  return {
    Authorization:
      "Bearer " +
      JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken,
    "Content-Type": "application/json",
  };
}

/** Store + localized dispensing channel (kiosk, counter, bedside, curbside). */
function formatDispensingLocation(row, t) {
  const store = row.store || "";
  const channel =
    getTypeByLang(row.deliverytype, t) || row.deliverytype || row.box || "";
  if (store && channel) {
    return `${store} – ${channel}`;
  }
  return row.dispensing_location || store || channel;
}

/** Accept only loadable image URLs; drop deleted placeholders / junk. */
function normalizeReportImageUrl(url) {
  if (url == null) return "";
  const s = String(url).trim();
  if (!s || s === "null" || s === "undefined") return "";
  if (/image_deleted\.png/i.test(s)) return "";
  if (/^https?:\/\//i.test(s) || /^data:image\//i.test(s)) return s;
  return "";
}

/**
 * Front is the latest type=0 capture; back is the latest type=1 capture.
 * API: id_image_url = front, id_image_url_2 = back.
 * Older APIs only return coalesced id_image_url (patient preferred) — show that as patient image.
 */
function resolveRowImages(row) {
  const patient = normalizeReportImageUrl(row.patient_image_url);
  const hasSplitPatientField = Object.prototype.hasOwnProperty.call(
    row,
    "patient_image_url"
  );
  const front = normalizeReportImageUrl(row.id_image_url);
  const back = normalizeReportImageUrl(row.id_image_url_2);

  if (!hasSplitPatientField) {
    return {
      patientImageUrl: front || back,
      idFrontImageUrl: "",
      idBackImageUrl: "",
    };
  }

  const idFrontImageUrl = front && front !== patient ? front : "";
  const idBackImageUrl =
    back && back !== patient && back !== idFrontImageUrl ? back : "";

  return {
    patientImageUrl: patient,
    idFrontImageUrl,
    idBackImageUrl,
  };
}

function rowHasAnyImage(row) {
  const images = resolveRowImages(row);
  return Boolean(
    images.patientImageUrl || images.idFrontImageUrl || images.idBackImageUrl
  );
}

/** Same Rx on this page may each hold one ID side; fill missing front/back. */
function collectIdImagesForRx(row, gridApi) {
  const images = resolveRowImages(row);
  let { idFrontImageUrl: front, idBackImageUrl: back } = images;
  const rx = row && row.rxno != null ? String(row.rxno).trim() : "";
  const pucode = row && row.pucode != null ? String(row.pucode).trim() : "";
  if (!rx || !gridApi || typeof gridApi.forEachNode !== "function") {
    return images;
  }
  gridApi.forEachNode((node) => {
    const other = node && node.data;
    if (!other || other === row) return;
    if (String(other.rxno || "").trim() !== rx) return;
    if (pucode && String(other.pucode || "").trim() && String(other.pucode).trim() !== pucode) {
      return;
    }
    const sibling = resolveRowImages(other);
    if (!front && sibling.idFrontImageUrl && sibling.idFrontImageUrl !== back) {
      front = sibling.idFrontImageUrl;
    }
    if (!back && sibling.idBackImageUrl && sibling.idBackImageUrl !== front) {
      back = sibling.idBackImageUrl;
    }
    if (!back && sibling.idFrontImageUrl && sibling.idFrontImageUrl !== front) {
      back = sibling.idFrontImageUrl;
    }
  });
  return { ...images, idFrontImageUrl: front, idBackImageUrl: back };
}

const PDF_BATCH_SIZE = 10;

function extractReportTbody(html) {
  const match = String(html || "").match(/<tbody[^>]*>([\s\S]*?)<\/tbody>/i);
  return match ? match[1] : "";
}

function mergeReportHtml(htmlParts, total) {
  const parts = (htmlParts || []).filter(Boolean);
  if (!parts.length) return "";
  const tbody = parts.map(extractReportTbody).join("");
  let html = parts[0];
  html = html.replace(/<tbody[^>]*>[\s\S]*?<\/tbody>/i, "<tbody>" + tbody + "</tbody>");
  if (total) {
    html = html.replace(
      /(<span id="csd-record-nums">)\s*\d+[–-]\d+(\s+(?:of|sur)\s+)\d+\s*(<\/span>)/i,
      "$11–" + total + "$2" + total + "$3"
    );
  }
  return html;
}

function printMergedReport(html) {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";

    let printed = false;
    let finished = false;
    let blobUrl = null;

    const finish = () => {
      if (finished) return;
      finished = true;
      resolve();
    };

    const cleanup = () => {
      try {
        iframe.onload = null;
        iframe.src = "about:blank";
      } catch (err) {
        /* no-op */
      }
      setTimeout(() => {
        try {
          if (iframe.parentNode) iframe.remove();
        } catch (err) {
          /* no-op */
        }
        if (blobUrl) {
          try {
            URL.revokeObjectURL(blobUrl);
          } catch (err) {
            /* no-op */
          }
        }
      }, 0);
    };

    const startPrint = () => {
      if (printed || finished) return;
      const win = iframe.contentWindow;
      const doc = iframe.contentDocument || (win && win.document);
      if (!win || !doc || !doc.body || !String(doc.body.innerHTML || "").trim()) {
        return;
      }
      printed = true;
      try {
        win.focus();
        win.print();
      } catch (err) {
        cleanup();
        finish();
        return;
      }
      finish();
      setTimeout(cleanup, 2000);
    };

    iframe.onload = startPrint;
    document.body.appendChild(iframe);
    try {
      blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      iframe.src = blobUrl;
    } catch (err) {
      const doc = iframe.contentDocument;
      if (!doc) {
        cleanup();
        finish();
        return;
      }
      doc.open();
      doc.write(html);
      doc.close();
    }
    setTimeout(startPrint, 1500);
  });
}

function isAlreadySignedS3Url(url) {
  return /[?&](X-Amz-Signature|Signature)=/i.test(url || "");
}

/**
 * Sign private report image URLs for the preview dialog.
 * No-ops when URLs are already signed (new API page response) or endpoint is unavailable.
 */
async function signReportImageUrls(urls) {
  const list = Array.isArray(urls) ? urls.map((u) => u || "") : [];
  if (!list.some(Boolean)) return list;
  if (list.filter(Boolean).every(isAlreadySignedS3Url)) return list;

  try {
    const response = await fetch(
      process.env.REACT_APP_BASEURL + "signReportImages",
      {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ urls: list }),
      }
    );
    if (!response.ok) return list;
    const data = await response.json();
    if (!data || data.Success === false || !Array.isArray(data.Results)) {
      return list;
    }
    // Preserve index alignment with the request urls array.
    return list.map((original, i) =>
      original ? data.Results[i] || "" : ""
    );
  } catch (e) {
    return list;
  }
}

function ControlledSubstanceDispensedReport({ t, i18n, classes }) {
  const filterRef = useRef(buildFilterRef());
  const gridApiRef = useRef(null);
  const pdfAbortRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [pdfDownload, setPdfDownload] = useState({
    open: false,
    from: 0,
    to: 0,
    total: 0,
    batch: 0,
    batches: 0,
    error: "",
  });
  const [idDialog, setIdDialog] = useState({
    open: false,
    idFrontImageUrl: "",
    idBackImageUrl: "",
    idFrontImageFailed: false,
    idBackImageFailed: false,
    patientImageUrl: "",
    patientImageFailed: false,
    title: "",
  });
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const clearReportLoader = useCallback(() => {
    setLoading(false);
    const api = gridApiRef.current;
    if (api && typeof api.hideOverlay === "function") {
      try {
        api.hideOverlay();
      } catch (err) {
        /* no-op */
      }
    }
  }, []);

  useEffect(() => {
    const hide = () => clearReportLoader();
    window.addEventListener("afterprint", hide);
    const onFocus = () => {
      const api = gridApiRef.current;
      const hasRows =
        api &&
        typeof api.getDisplayedRowCount === "function" &&
        api.getDisplayedRowCount() > 0;
      if (hasRows) hide();
    };
    window.addEventListener("focus", onFocus);
    let mql;
    const onPrintChange = (event) => {
      if (event && event.matches === false) hide();
    };
    try {
      mql = window.matchMedia("print");
      if (mql.addEventListener) mql.addEventListener("change", onPrintChange);
      else if (mql.addListener) mql.addListener(onPrintChange);
    } catch (err) {
      /* no-op */
    }
    return () => {
      window.removeEventListener("afterprint", hide);
      window.removeEventListener("focus", onFocus);
      if (!mql) return;
      if (mql.removeEventListener) mql.removeEventListener("change", onPrintChange);
      else if (mql.removeListener) mql.removeListener(onPrintChange);
    };
  }, [clearReportLoader]);

  const convertToTimeZone = useCallback((requestDate) => {
    if (!requestDate) return "";
    const currentUserLang = localStorage.getItem("i18nextLng");
    let zone = sessionStorage.getItem("timeZone");
    const date = moment(new Date(requestDate));
    if (["null", null, "undefined", undefined].includes(zone)) {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    const zoneName = zone.includes(" ") ? zone.substring(zone.lastIndexOf(" ") + 1) : zone;
    return currentUserLang === "en"
      ? date.tz(zoneName).format("MM/DD/YYYY hh:mm:ss A")
      : date.tz(zoneName).format("DD/MM/YYYY hh:mm:ss A");
  }, []);

  const onPaginationChanged = useCallback((params) => {
    if (!params.api) return;
    const displayed =
      typeof params.api.getDisplayedRowCount === "function"
        ? params.api.getDisplayedRowCount()
        : 0;
    if (displayed > 0) setLoading(false);
    const newPageSize = params.api.paginationGetPageSize();
    if (newPageSize && filterRef.current.pageSize !== newPageSize) {
      filterRef.current.pageSize = newPageSize;
    }
  }, []);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    const datasource = createServerSideDatasource({
      sortMap: PHARMACY_REPORT_SORT_MAP,
      filterBody: filterRef.current,
      applyFilterModel: (body, filterModel) => {
        applyControlledSubstanceReportFilterModel(body, filterModel);
      },
      pageZeroBased: true,
      getPageSize: () => filterRef.current.pageSize || 10,
      // The SSRM datasource is the only loader for this report. A second
      // direct fetch on mount used to race this one against the same slow
      // endpoint and push it past the API Gateway timeout (504).
      fetchPage: (body) =>
        fetch(process.env.REACT_APP_BASEURL + "getControlledSubstanceDispensed", {
          method: "post",
          withCredentials: true,
          headers: authHeaders(),
          body: JSON.stringify({
            ...body,
            timezone: getReportTimezone(),
          }),
        })
          .then((r) => {
            if (!r.ok) {
              throw new Error(`getControlledSubstanceDispensed failed: ${r.status}`);
            }
            return r.json();
          })
          .then((data) => {
            if (data && data.results) return data;
            if (Array.isArray(data)) return { results: data, total: data.length };
            return {
              results: (data && data.rows) || [],
              total: (data && data.total) || 0,
            };
          }),
      onSuccess: (results) => {
        setLoading(false);
        setIsButtonDisabled(!results || results.length === 0);
      },
      onError: () => {
        setLoading(false);
        setIsButtonDisabled(true);
      },
    });
    attachReportServerSideDatasource(
      params,
      datasource,
      filterRef.current,
      "pickupdate"
    );
  }, []);

  const exportCSV = () => {
    setLoading(true);
    fetch(process.env.REACT_APP_BASEURL + "CSV/ControlledSubstanceDispensedReport", {
      method: "POST",
      withCredentials: true,
      headers: authHeaders(),
      body: JSON.stringify({
        ...filterRef.current,
        timezone: getReportTimezone(),
      }),
    })
      .then((response) => {
        try {
          return response.json();
        } catch (err) {
          return null;
        }
      })
      .then((csvData) => {
        setLoading(false);
        const list =
          csvData && csvData.Results && csvData.Results.length > 0 ? csvData.Results : [];
        if (!list.length) {
          return;
        }
        const headers = [
          [
            "Dispensing location",
            "Item",
            "Patient first",
            "Patient last",
            "Rx #",
            "Fill",
            "Schedule",
            "ID number",
            "ID first name",
            "ID last name",
            "Issuing org",
            "ID expiration",
            "ID type",
            "Pickup date/time",
            "Care team member",
            "ID image URL",
            "ID image URL 2",
            "Patient image URL",
          ].join(","),
        ];
        const lines = list.map((b) =>
          [
            formatDispensingLocation(b, t).replace(/,/g, " "),
            (b.drugname || "").replace(/,/g, " "),
            b.firstname || "",
            b.lastname || "",
            b.rxno || "",
            b.fillid != null ? String(b.fillid) : "",
            b.dea_schedule != null ? String(b.dea_schedule) : "",
            b.pdrivers_license_no || "",
            b.pfirst_name || "",
            b.plast_name || "",
            b.selectedstatecode || "",
            b.expdate != null ? String(b.expdate) : "",
            (b.card_type || "").replace(/,/g, " "),
            b.pickupdate ? convertToTimeZone(b.pickupdate) : "",
            (b.care_team_member || "").replace(/,/g, " "),
            b.id_image_url || "",
            b.id_image_url_2 || "",
            b.patient_image_url || "",
          ].join(",")
        );
        generateCSV({
          data: [...headers, ...lines].join("\n"),
          fileName: "ControlledSubstanceDispensed.csv",
          fileType: "text/csv",
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const exportPDF = async () => {
    if (pdfAbortRef.current) {
      try {
        pdfAbortRef.current.abort();
      } catch (err) {
        /* no-op */
      }
    }
    const AbortCtl = window.AbortController;
    const abort = AbortCtl
      ? new AbortCtl()
      : { signal: { aborted: false }, abort: function abortDownload() { this.signal.aborted = true; } };
    pdfAbortRef.current = abort;
    clearReportLoader();

    setPdfDownload({
      open: true,
      from: 0,
      to: 0,
      total: 0,
      batch: 0,
      batches: 0,
      error: "",
    });

    const pdfError = t("Report.Picked.PdfError", {
      defaultValue: "Failed to generate PDF",
    });

    try {
      let pageNo = 0;
      let total = null;
      const htmlParts = [];

      while (!abort.signal.aborted) {
        const fetchOpts = {
          method: "POST",
          withCredentials: true,
          headers: authHeaders(),
          body: JSON.stringify({
            ...filterRef.current,
            timezone: getReportTimezone(),
            locale: i18n.language,
            pageNo,
            pageSize: PDF_BATCH_SIZE,
          }),
        };
        if (AbortCtl) fetchOpts.signal = abort.signal;
        const response = await fetch(
          process.env.REACT_APP_BASEURL + "CSV/ControlledSubstanceDispensedReportPDF",
          fetchOpts
        );

        const contentType = response.headers.get("Content-Type") || "";
        if (contentType.includes("application/pdf")) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "ControlledSubstanceDispensed.pdf";
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
          setPdfDownload((prev) => ({ ...prev, open: false }));
          return;
        }

        const text = await response.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch (err) {
          throw new Error(text || pdfError);
        }
        if (!json || json.Success === false || !json.Results) {
          throw new Error(
            (json && (json.Error || json.Message)) || text || pdfError
          );
        }

        const payload = json.Results;
        const html = payload.html || payload.Html;
        total = Number(payload.total != null ? payload.total : total || 0);
        const from = Number(payload.recordFrom || 0);
        const to = Number(payload.recordTo || 0);
        const batches = Math.max(1, Math.ceil((total || 0) / PDF_BATCH_SIZE));

        setPdfDownload({
          open: true,
          from,
          to,
          total: total || 0,
          batch: pageNo + 1,
          batches,
          error: "",
        });

        if (html) htmlParts.push(html);

        if (!total || to >= total) break;
        pageNo += 1;
      }

      if (abort.signal.aborted) return;

      const merged = mergeReportHtml(htmlParts, total);
      if (!merged) {
        throw new Error(pdfError);
      }

      setPdfDownload((prev) => ({ ...prev, open: false }));
      clearReportLoader();
      await printMergedReport(merged);
    } catch (err) {
      if (err && err.name === "AbortError") {
        setPdfDownload({
          open: false,
          from: 0,
          to: 0,
          total: 0,
          batch: 0,
          batches: 0,
          error: "",
        });
        return;
      }
      setPdfDownload((prev) => ({
        ...prev,
        open: true,
        error:
          pdfError + (err && err.message ? ": " + err.message : ""),
      }));
    } finally {
      clearReportLoader();
      if (pdfAbortRef.current === abort) {
        pdfAbortRef.current = null;
      }
    }
  };

  return (
    <Paper className={classes.paper}>
      {loading ? (
        <div className={classes.spinner}>
          <img className={classes.spinnerImage} src={spinner} alt="loading" />
        </div>
      ) : null}

      <div id="react-no-print">
        <Dialog
          open={idDialog.open}
          onClose={() => setIdDialog({ ...idDialog, open: false })}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>{idDialog.title}</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                justifyContent: "center",
                alignItems: "flex-start",
              }}
            >
              {[
                idDialog.idFrontImageUrl
                  ? {
                      key: "id-front",
                      url: idDialog.idFrontImageUrl,
                      failed: idDialog.idFrontImageFailed,
                      onError: () =>
                        setIdDialog((prev) => ({ ...prev, idFrontImageFailed: true })),
                      label: t("Report.ControlledSubstance.IdCaptureImage", {
                        defaultValue: "ID image",
                      }),
                    }
                  : null,
                idDialog.idBackImageUrl
                  ? {
                      key: "id-back",
                      url: idDialog.idBackImageUrl,
                      failed: idDialog.idBackImageFailed,
                      onError: () =>
                        setIdDialog((prev) => ({ ...prev, idBackImageFailed: true })),
                      label: t("Report.ControlledSubstance.IdCaptureImage", {
                        defaultValue: "ID image",
                      }),
                    }
                  : null,
                idDialog.patientImageUrl
                  ? {
                      key: "patient",
                      url: idDialog.patientImageUrl,
                      failed: idDialog.patientImageFailed,
                      onError: () =>
                        setIdDialog((prev) => ({ ...prev, patientImageFailed: true })),
                      label: t("Report.ControlledSubstance.PatientImage", {
                        defaultValue: "Patient image",
                      }),
                    }
                  : null,
              ]
                .filter((tile) => tile && tile.url)
                .map((tile) => (
                  <Box
                    key={tile.key}
                    sx={{ textAlign: "center", flex: "1 1 280px", maxWidth: 420 }}>
                    <Box
                      component="div"
                      sx={{ mb: 1, fontWeight: 600, color: "rgba(0,0,0,0.6)", fontSize: "0.875rem" }}>
                      {tile.label}
                    </Box>
                    {tile.failed ? (
                      <Box sx={{ py: 3, color: "rgba(0,0,0,0.54)" }}>
                        {t("Report.ControlledSubstance.ImageLoadFailed", {
                          defaultValue: "Unable to load image",
                        })}
                      </Box>
                    ) : (
                      <img
                        src={tile.url}
                        alt={tile.label}
                        style={{ maxWidth: "100%", maxHeight: 420, objectFit: "contain" }}
                        onError={tile.onError}
                      />
                    )}
                  </Box>
                ))}
              {!idDialog.idFrontImageUrl &&
              !idDialog.idBackImageUrl &&
              !idDialog.patientImageUrl ? (
                <Box sx={{ py: 4, color: "rgba(0,0,0,0.54)" }}>
                  {t("Report.ControlledSubstance.NoImageAvailable", {
                    defaultValue: "No image available",
                  })}
                </Box>
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIdDialog({ ...idDialog, open: false })}
              color="primary"
              className={classes.primaryButton}>
              {t("Report.Picked.Temperature.CloseButton", { defaultValue: "Close" })}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={pdfDownload.open}
          onClose={() => {
            if (pdfDownload.error) {
              setPdfDownload((prev) => ({ ...prev, open: false, error: "" }));
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t("Report.ControlledSubstance.PdfDownloadTitle", {
              defaultValue: "Preparing PDF",
            })}
          </DialogTitle>
          <DialogContent>
            {pdfDownload.error ? (
              <Typography color="error">{pdfDownload.error}</Typography>
            ) : (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {pdfDownload.total
                    ? t("Report.ControlledSubstance.PdfDownloadProgress", {
                        defaultValue: "Loading records {{from}}–{{to}} of {{total}} (batch {{batch}} of {{batches}})",
                        from: pdfDownload.from,
                        to: pdfDownload.to,
                        total: pdfDownload.total,
                        batch: pdfDownload.batch,
                        batches: pdfDownload.batches,
                      })
                    : t("Report.ControlledSubstance.PdfDownloadStarting", {
                        defaultValue: "Preparing the first batch of 10 records…",
                      })}
                </Typography>
                <LinearProgress
                  variant={pdfDownload.total ? "determinate" : "indeterminate"}
                  value={
                    pdfDownload.total
                      ? Math.min(
                          100,
                          Math.round(
                            ((pdfDownload.to || 0) / pdfDownload.total) * 100
                          )
                        )
                      : 0
                  }
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {pdfDownload.error ? (
              <Button
                onClick={() =>
                  setPdfDownload((prev) => ({ ...prev, open: false, error: "" }))
                }
                color="primary"
                className={classes.primaryButton}>
                {t("Report.Picked.Temperature.CloseButton", { defaultValue: "Close" })}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  if (pdfAbortRef.current) {
                    pdfAbortRef.current.abort();
                  }
                }}
                color="primary">
                {t("Global.Button.Cancel", { defaultValue: "Cancel" })}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </div>

            <ReportToolbar>
              <Tooltip title={t("Report.Picked.ExportPdf", { defaultValue: "Export PDF" })}>
                <IconButton
                  style={{ marginRight: "4px" }}
                  aria-label="ExportPDF"
                  disabled={isButtonDisabled || pdfDownload.open}
                  onClick={exportPDF}>
                  <PictureAsPdfIcon style={{ height: "28px", width: "28px" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("Report.Picked.ExportCSVTooltip")}>
                <IconButton
                  style={{ marginRight: "0" }}
                  aria-label="ExportCSV"
                  onClick={exportCSV}
                  disabled={isButtonDisabled}>
                  <img alt="download" src={download} style={{ height: "32px", width: "32px", cursor: "pointer" }} />
                </IconButton>
              </Tooltip>
            </ReportToolbar>

            <div className={classes.tableWrapper}>
      <ILocalAGGrid
        columnDefs={[
          {
            field: "dispensing_location",
            headerName: t("Report.ControlledSubstance.DispensingLocation"),
            colId: "dispensing_location",
            valueGetter: (p) => formatDispensingLocation(p.data || {}, t),
            filterParams: {
              resolveOptionNames: resolveControlledSubstanceDispensingLocationOptions,
            },
          },
          { field: "drugname", headerName: t("Report.ControlledSubstance.Item"), colId: "drugname" },
          {
            field: "firstname",
            headerName: t("Report.ControlledSubstance.Patient"),
            colId: "patient",
            valueGetter: (p) =>
              [p.data && p.data.firstname, p.data && p.data.lastname].filter(Boolean).join(" "),
          },
          {
            field: "rxno",
            headerName: t("Report.ControlledSubstance.RxNumber"),
            colId: "rxno",
            valueGetter: (p) => {
              const row = p.data || {};
              return row.fillid != null && row.fillid !== ""
                ? `${row.rxno} / ${row.fillid}`
                : row.rxno || "";
            },
          },
          {
            field: "dea_schedule",
            headerName: t("Report.ControlledSubstance.Schedule"),
            colId: "dea_schedule",
            filter: "agTextColumnFilter",
            valueGetter: (p) => {
              const row = p.data || {};
              return row.dea_schedule != null && row.dea_schedule !== ""
                ? String(row.dea_schedule)
                : "";
            },
          },
          { field: "pdrivers_license_no", headerName: t("Report.ControlledSubstance.IdNumber"), colId: "pdrivers_license_no" },
          {
            field: "pfirst_name",
            headerName: t("Report.ControlledSubstance.NameOnId"),
            colId: "id_name",
            valueGetter: (p) =>
              [p.data && p.data.pfirst_name, p.data && p.data.plast_name].filter(Boolean).join(" "),
          },
          { field: "selectedstatecode", headerName: t("Report.ControlledSubstance.IssuingOrg"), colId: "selectedstatecode" },
          { field: "expdate", headerName: t("Report.ControlledSubstance.IdExpiration"), colId: "expdate" },
          { field: "card_type", headerName: t("Report.ControlledSubstance.IdType"), colId: "card_type" },
          withDateColumnFilter({
            field: "pickupdate",
            headerName: t("Report.ControlledSubstance.PickupDate"),
            colId: "pickupdate",
            valueGetter: (p) =>
              p.data && p.data.pickupdate ? convertToTimeZone(p.data.pickupdate) : "",
          }),
          { field: "care_team_member", headerName: t("Report.ControlledSubstance.CareTeam"), colId: "care_team_member",
            valueGetter: (p) => (p.data && p.data.care_team_member) || (p.data && p.data.username) || "" },
          {
            field: "id_image_url",
            headerName: t("Report.ControlledSubstance.IdImage"),
            colId: "_id",
            filter: false,
            sortable: false,
            cellRenderer: (params) => {
              const row = params.data || {};
              if (!rowHasAnyImage(row)) return "—";
              return (
                <Tooltip title={t("Report.ControlledSubstance.ViewId")}>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const images = collectIdImagesForRx(
                        row,
                        params.api || gridApiRef.current
                      );
                      setIdDialog({
                        open: true,
                        idFrontImageUrl: images.idFrontImageUrl,
                        idBackImageUrl: images.idBackImageUrl,
                        idFrontImageFailed: false,
                        idBackImageFailed: false,
                        patientImageUrl: images.patientImageUrl,
                        patientImageFailed: false,
                        title: t("Report.ControlledSubstance.IdPreviewTitle"),
                      });
                      const signed = await signReportImageUrls([
                        images.idFrontImageUrl,
                        images.idBackImageUrl,
                        images.patientImageUrl,
                      ]);
                      setIdDialog((prev) => ({
                        ...prev,
                        idFrontImageUrl: signed[0] || images.idFrontImageUrl,
                        idBackImageUrl: signed[1] || images.idBackImageUrl,
                        patientImageUrl: signed[2] || images.patientImageUrl,
                        idFrontImageFailed:
                          Boolean(images.idFrontImageUrl) && !signed[0],
                        idBackImageFailed:
                          Boolean(images.idBackImageUrl) && !signed[1],
                        patientImageFailed:
                          Boolean(images.patientImageUrl) && !signed[2],
                      }));
                    }}
                  >
                    <ImageSearchIcon />
                  </IconButton>
                </Tooltip>
              );
            },
          },
        ]}
        defaultColDef={getDefaultColDef()}
        rowModelType="serverSide"
        cacheBlockSize={filterRef.current.pageSize || 10}
        paginationPageSize={filterRef.current.pageSize || 10}
        paginationPageSizeSelector={[10, 20, 25]}
        rowHeight={40}
        getRowId={(params) =>
          `${(params.data && params.data.pmid) || ""}-${(params.data && params.data.rxid) || ""}-${(params.data && params.data.id) || ""}`
        }
        onGridReady={onGridReady}
        onPaginationChanged={onPaginationChanged}
      />

      </div>

    </Paper>
  );
}

ControlledSubstanceDispensedReport.propTypes = {
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(
  withTranslation()(ControlledSubstanceDispensedReport)
);



// WEBPACK FOOTER //
// ./src/components/reports/controlled-substance-dispensed/index.js